import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, LayoutDashboard, KeyRound, ScanSearch, Download, LogOut, Radar, X, AlertTriangle, Layers, Users } from "lucide-react";
import { VERDICTS } from "@/components/RiskBadge";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  { to: "/tokens", label: "Scan Tokens", icon: KeyRound, id: "nav-tokens" },
  { to: "/scans", label: "Scan Results", icon: ScanSearch, id: "nav-scans" },
  { to: "/sessions", label: "Sessions", icon: Layers, id: "nav-sessions" },
  { to: "/clients", label: "Clients", icon: Users, id: "nav-clients", adminOnly: true },
  { to: "/downloads", label: "Scanner Agent", icon: Download, id: "nav-downloads" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [liveAlerts, setLiveAlerts] = useState([]);
  const wsRef = useRef(null);

  useEffect(() => {
    let closed = false;
    const connect = () => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = base.replace(/^http/, "ws") + "/api/ws/alerts";
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        return;
      }
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "scan" && msg.verdict !== "clear") {
            const item = { ...msg, key: `${msg.scan_id}-${Date.now()}` };
            setLiveAlerts((a) => [item, ...a].slice(0, 4));
            setTimeout(() => {
              setLiveAlerts((a) => a.filter((x) => x.key !== item.key));
            }, 12000);
          }
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        if (!closed) setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      closed = true;
      wsRef.current?.close();
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#070B14] soc-grid">
      <aside className="w-64 shrink-0 border-r border-white/8 glass flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-white/8">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <ShieldAlert className="w-7 h-7 text-cyan-400" />
            </div>
            <div>
              <div className="font-head font-bold text-lg leading-none tracking-tight">SAT</div>
              <div className="eyebrow mt-1">SCANNER AGENT TOOL</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/25"
                    : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/8">
          {user?.role === "client" && (
            <div className="mx-3 mb-3 bg-[#0B1220] border border-white/8 rounded-lg px-3 py-2.5" data-testid="quota-badge">
              <div className="eyebrow mb-1">TOKEN QUOTA</div>
              <div className="font-mono text-sm text-cyan-300">
                {user.token_quota == null
                  ? `${user.tokens_generated} used · unlimited`
                  : `${user.tokens_generated} / ${user.token_quota} used`}
              </div>              {user.token_quota != null && (
                <div className="h-1.5 bg-white/5 rounded mt-2 overflow-hidden">
                  <div
                    className="h-full bg-cyan-400"
                    style={{ width: `${Math.min(100, (user.tokens_generated / Math.max(1, user.token_quota)) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}
          <div className="px-3 py-2 mb-2">
            <div className="text-sm text-slate-200 font-medium truncate">{user?.name || "Admin"}</div>
            <div className="text-xs text-slate-500 truncate">
              {user?.email || user?.username} · <span className="uppercase font-mono">{user?.role}</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <LogOut style={{ width: 18, height: 18 }} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        <header className="h-16 border-b border-white/8 glass sticky top-0 z-20 flex items-center justify-between px-8">
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Radar className="w-4 h-4 text-emerald-400" />
            <span className="font-mono text-xs">System online</span>
            <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot ml-1" />
          </div>
          <div className="eyebrow">OFFLINE RULE-BASED DETECTION ENGINE v1.0</div>
        </header>
        <div className="p-8 max-w-[1400px] mx-auto">{children}</div>
      </main>

      {/* Live real-time high-risk scan alerts */}
      <div className="fixed top-20 right-6 z-50 space-y-3 w-[360px]" data-testid="live-alerts">
        {liveAlerts.map((a) => {
          const v = VERDICTS[a.verdict] || VERDICTS.high_risk;
          return (
            <div
              key={a.key}
              onClick={() => {
                navigate(`/scans/${a.scan_id}`);
                setLiveAlerts((list) => list.filter((x) => x.key !== a.key));
              }}
              data-testid="live-alert-toast"
              className="card-soc p-4 cursor-pointer fade-up shadow-2xl"
              style={{ borderColor: v.border }}
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: v.text }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] font-semibold tracking-wider" style={{ color: v.text }}>
                      {v.label} DETECTED
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLiveAlerts((list) => list.filter((x) => x.key !== a.key));
                      }}
                      className="text-slate-500 hover:text-slate-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-sm text-slate-100 font-medium truncate mt-1">{a.candidate_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Risk {a.risk_score}/100 · {a.detection_count} detection{a.detection_count === 1 ? "" : "s"} · click to inspect
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
